import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { addTester, createAdminUser, deleteAdminUser, getUserDeletion, listAdminUsers, removeTester } from '../../../services/adminApi';
import useConfirm from '../shell/useConfirm';
import { Tag } from '../catalog/ui';
import '../catalog/catalog.css';
import '../orders/orders.css';
import '../books/people.css';

// Utilisateurs : comptes, testeurs, administrateurs et visiteurs anonymes, avec dernière connexion et nombre de livres.
// Inviter ou retirer un testeur se fait ici. Jamais d'adresse postale ni de téléphone.
// Le lieu est le PAYS de la dernière commande : aucune géolocalisation n'est rattachée aux comptes.

const TYPES = ['compte', 'testeur', 'admin', 'anonyme'];
const TON_TYPE = { compte: '', testeur: 'warn', admin: 'ok', anonyme: 'mute' };

function Kpi({ label, value, note }) {
  return (
    <div className="adm-kpi">
      <span>{label}</span>
      <b>{value}</b>
      {note && <small>{note}</small>}
    </div>
  );
}

export default function UsersSection() {
  const { t, i18n } = useTranslation('admin');
  const [params, setParams] = useSearchParams();
  const type = params.get('type') || '';
  const q = params.get('q') || '';
  const page = Number(params.get('page')) || 1;
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [saisie, setSaisie] = useState(q);
  const [invitation, setInvitation] = useState(false);
  const [adresse, setAdresse] = useState('');
  const [note, setNote] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [creation, setCreation] = useState(false);
  const [nouvelleAdresse, setNouvelleAdresse] = useState('');
  const [nouveauNom, setNouveauNom] = useState('');
  const [confirmer, confirmation] = useConfirm();
  const derniere = useRef(0);

  const changer = (modifs) => {
    const suivant = new URLSearchParams(params);
    Object.entries(modifs).forEach(([cle, valeur]) => { if (valeur) suivant.set(cle, valeur); else suivant.delete(cle); });
    if (!('page' in modifs)) suivant.delete('page');
    setParams(suivant, { replace: true });
  };

  const charger = useCallback(async () => {
    const numero = ++derniere.current;
    try {
      const r = await listAdminUsers({ type, q, page });
      if (numero === derniere.current) { setData(r); setErreur(''); }
    } catch (err) {
      if (numero === derniere.current) setErreur(err?.message || t('users.loadFailed'));
    }
  }, [type, q, page, t]);
  useEffect(() => { charger(); }, [charger]);

  useEffect(() => { setSaisie(q); }, [q]);
  useEffect(() => {
    if (saisie === q) return undefined;
    const minuteur = setTimeout(() => changer({ q: saisie.trim() }), 300);
    return () => clearTimeout(minuteur);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saisie]);

  const inviter = async (evenement) => {
    evenement.preventDefault();
    if (!adresse.trim()) return;
    setEnCours(true);
    setErreur('');
    setMessage('');
    try {
      await addTester(adresse.trim(), note.trim());
      setMessage(t('users.invited', { email: adresse.trim() }));
      setAdresse('');
      setNote('');
      setInvitation(false);
      await charger();
    } catch (err) {
      setErreur(err?.message || t('users.inviteFailed'));
    } finally {
      setEnCours(false);
    }
  };

  const retirer = async (ligne) => {
    if (!(await confirmer({
      title: t('users.removeTitle', { email: ligne.email }),
      message: t('users.removeBody'),
      confirmLabel: t('users.removeYes')
    }))) return;
    setEnCours(true);
    setErreur('');
    setMessage('');
    try {
      await removeTester(ligne.email);
      setMessage(t('users.removed', { email: ligne.email }));
      await charger();
    } catch (err) {
      setErreur(err?.message || t('users.removeFailed'));
    } finally {
      setEnCours(false);
    }
  };

  const creer = async (evenement) => {
    evenement.preventDefault();
    if (!nouvelleAdresse.trim()) return;
    setEnCours(true);
    setErreur('');
    setMessage('');
    try {
      const cree = await createAdminUser(nouvelleAdresse.trim(), nouveauNom.trim());
      setMessage(t('users.created', { email: cree.email }));
      setNouvelleAdresse('');
      setNouveauNom('');
      setCreation(false);
      await charger();
    } catch (err) {
      setErreur(err?.message || t('users.createFailed'));
    } finally {
      setEnCours(false);
    }
  };

  // Le serveur dit d'abord ce qui serait effacé (ou pourquoi c'est refusé) ; on ne demande confirmation que si c'est possible.
  const supprimer = async (ligne) => {
    const qui = ligne.email || (ligne.id ? ligne.id.slice(0, 8) : '');
    setErreur('');
    setMessage('');
    let bilan;
    try {
      bilan = await getUserDeletion(ligne.id);
    } catch (err) {
      setErreur(err?.message || t('users.deleteFailed'));
      return;
    }
    if (!bilan.possible) {
      setErreur(bilan.reasons.join(' '));
      return;
    }
    const details = [];
    if (bilan.books) details.push(t('users.deleteBooks', { count: bilan.books }));
    if (bilan.orders) details.push(t('users.deleteOrders', { count: bilan.orders }));
    if (!details.length) details.push(t('users.deleteNothing'));
    if (!(await confirmer({ title: t('users.deleteTitle', { who: qui }), message: t('users.deleteBody'), details, confirmLabel: t('users.deleteYes') }))) return;
    setEnCours(true);
    try {
      await deleteAdminUser(ligne.id);
      setMessage(t('users.deleted', { who: qui }));
      await charger();
    } catch (err) {
      setErreur(err?.message || t('users.deleteFailed'));
    } finally {
      setEnCours(false);
    }
  };

  const stats = data && data.stats;
  const premier = data ? (data.page - 1) * data.perPage + 1 : 0;
  const dernier = data ? Math.min(data.page * data.perPage, data.total) : 0;
  const compteur = (cle) => (stats ? (cle === 'testeur' ? stats.testers : stats.types[cle]) : null);
  const date = (valeur) => (valeur ? new Date(valeur).toLocaleDateString(i18n.language) : null);

  return (
    <>
      {stats && (
        <div className="adm-kpis">
          <Kpi label={t('users.kpi.accounts')} value={stats.accounts} note={t('users.kpi.accountsNote', { count: stats.newThisWeek })} />
          <Kpi label={t('users.kpi.testers')} value={stats.testers} note={t('users.kpi.testersNote')} />
          <Kpi label={t('users.kpi.active')} value={stats.activeThisWeek} note={stats.accounts ? t('users.kpi.activeNote', { percent: Math.round((stats.activeThisWeek / stats.accounts) * 100) }) : ''} />
          <Kpi label={t('users.kpi.anonymousToClean')} value={stats.anonymousToClean} note={t('users.kpi.anonymousToCleanNote')} />
        </div>
      )}

      <div className="adm-actions">
        <form className="adm-orders-search" role="search" onSubmit={(e) => { e.preventDefault(); changer({ q: saisie.trim() }); }}>
          <label className="adm-sr" htmlFor="adm-users-q">{t('users.searchLabel')}</label>
          <input id="adm-users-q" className="adm-input" type="search" value={saisie} placeholder={t('users.searchPlaceholder')} onChange={(e) => setSaisie(e.target.value)} />
        </form>
        <span className="adm-sp" />
        <button type="button" className="btn btn-outline btn-sm" onClick={() => setCreation((v) => !v)} aria-expanded={creation}>{t('users.create')}</button>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setInvitation((v) => !v)} aria-expanded={invitation}>{t('users.invite')}</button>
      </div>

      {creation && (
        <form className="adm-card adm-invite" onSubmit={creer}>
          <p className="adm-note">{t('users.createHelp')}</p>
          <div className="adm-actions">
            <label className="adm-sr" htmlFor="adm-create-email">{t('users.createEmail')}</label>
            <input id="adm-create-email" className="adm-input" type="email" required value={nouvelleAdresse} onChange={(e) => setNouvelleAdresse(e.target.value)} placeholder={t('users.inviteEmailPlaceholder')} />
            <label className="adm-sr" htmlFor="adm-create-name">{t('users.createName')}</label>
            <input id="adm-create-name" className="adm-input" maxLength={120} value={nouveauNom} onChange={(e) => setNouveauNom(e.target.value)} placeholder={t('users.createNamePlaceholder')} />
            <button type="submit" className="btn btn-primary btn-sm" disabled={enCours || !nouvelleAdresse.trim()}>{t('users.createSend')}</button>
          </div>
        </form>
      )}

      {invitation && (
        <form className="adm-card adm-invite" onSubmit={inviter}>
          <p className="adm-note">{t('users.inviteHelp')}</p>
          <div className="adm-actions">
            <label className="adm-sr" htmlFor="adm-invite-email">{t('users.inviteEmail')}</label>
            <input id="adm-invite-email" className="adm-input" type="email" required value={adresse} onChange={(e) => setAdresse(e.target.value)} placeholder={t('users.inviteEmailPlaceholder')} />
            <label className="adm-sr" htmlFor="adm-invite-note">{t('users.inviteNote')}</label>
            <input id="adm-invite-note" className="adm-input" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('users.inviteNotePlaceholder')} />
            <button type="submit" className="btn btn-primary btn-sm" disabled={enCours || !adresse.trim()}>{t('users.inviteSend')}</button>
          </div>
        </form>
      )}

      <div className="adm-chips" role="group" aria-label={t('users.filters')}>
        <button type="button" className="adm-chip" aria-pressed={!type} onClick={() => changer({ type: '' })}>{t('users.all')} {stats && <b>{Object.values(stats.types).reduce((a, b) => a + b, 0)}</b>}</button>
        {TYPES.map((k) => (
          <button key={k} type="button" className="adm-chip" aria-pressed={type === k} onClick={() => changer({ type: type === k ? '' : k })}>
            {t(`users.types.${k}`)} {stats && <b>{compteur(k)}</b>}
          </button>
        ))}
      </div>

      {confirmation}
      {message && <p className="adm-note adm-flash" role="status">{message}</p>}
      {erreur && <p className="adm-error" role="alert">{erreur}</p>}
      {!data && !erreur && <p className="adm-state" role="status">{t('catalog.common.loading')}</p>}
      {data && data.users.length === 0 && <p className="adm-note">{t('users.empty')}</p>}

      {data && data.users.length > 0 && (
        <div className="adm-tbl">
          <table>
            <thead>
              <tr>
                <th>{t('users.cols.name')}</th><th>{t('users.cols.country')}</th><th>{t('users.cols.lastSignIn')}</th>
                <th className="num">{t('users.cols.books')}</th><th>{t('users.cols.type')}</th><th><span className="adm-sr">{t('users.cols.actions')}</span></th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.id || `invite-${u.email}`}>
                  <td>
                    <b>{u.type === 'anonyme' ? t('users.anonymousVisitor') : (u.name || u.email || '—')}</b>
                    <small>{u.type === 'anonyme' ? (u.id ? u.id.slice(0, 8) : '') : (u.name ? u.email : '')}</small>
                  </td>
                  <td>{u.country || '—'}</td>
                  <td>{u.invited ? <span className="adm-note">{t('users.neverSignedIn')}</span> : (date(u.lastSignInAt) || '—')}<small>{date(u.createdAt) ? t(u.invited ? 'users.invitedOn' : 'users.createdOn', { date: date(u.createdAt) }) : ''}</small></td>
                  <td className="num">{u.books || '—'}</td>
                  <td>
                    <Tag tone={TON_TYPE[u.type]}>{t(`users.types.${u.type}`)}</Tag>
                    <small>{u.invited ? t('users.invitedTag') : (u.testerSource === 'server' ? t('users.fromServer') : '')}</small>
                  </td>
                  <td>
                    <div className="adm-actions">
                      {u.testerSource === 'managed' && <button type="button" className="btn btn-outline btn-sm" onClick={() => retirer(u)} disabled={enCours}>{t('users.remove')}</button>}
                      {u.id && u.type !== 'admin' && <button type="button" className="btn btn-outline btn-sm" onClick={() => supprimer(u)} disabled={enCours}>{t('users.delete')}</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.total > data.perPage && (
        <nav className="adm-pager" aria-label={t('users.pagination')}>
          <span className="adm-note">{t('orders.range', { from: premier, to: dernier, total: data.total })}</span>
          <span className="adm-sp" />
          <button type="button" className="btn btn-outline btn-sm" disabled={data.page <= 1} onClick={() => changer({ page: String(data.page - 1) })}>{t('orders.previous')}</button>
          <button type="button" className="btn btn-outline btn-sm" disabled={dernier >= data.total} onClick={() => changer({ page: String(data.page + 1) })}>{t('orders.next')}</button>
        </nav>
      )}
      <p className="adm-note">{t('users.privacy')}</p>
    </>
  );
}
